<template>
  <div class="transportation-accounts-vehicles">
    <div v-if="$router.currentRoute.name === 'transportation-accounts-vehicles'">
      <div class="layout justify-start">
        <v-btn :to="{name: 'transportation-accounts-vehicles-realtime', params: { accountId }}" class="mr-2">
          <v-icon small>fal fa-location-circle</v-icon> &nbsp; Realtime Map
        </v-btn>
        <v-btn v-if="hasUserType('admin') && !isOnlyInvestor" class="mr-2" @click="scannerProvisionDialog = true">
          <v-icon small>fal fa-barcode-read</v-icon> &nbsp; Provision Scanner
        </v-btn>
      </div>
      <v-dialog v-model="scannerProvisionDialog" max-width="720px">
        <v-card>
          <v-card-title class="justify-center">
            <h2>Provision Barcode Scanner</h2>
          </v-card-title>
          <v-card-text>
            <p>
              Scan this Zebra DS9308 programming barcode to set the scanner USB device type to
              <strong>USB CDC Host</strong>.
            </p>
            <p><small>
              This makes the scanner present barcode reads over a USB CDC serial connection instead of
              acting like a keyboard, which is the mode expected by the Raspberry Pi board and alight system.
              The scanner may reset and beep after the mode change.
              </small>
            </p>
            <div class="scanner-provision-dialog__barcode-container">
              <img
                src="@/assets/ds9308-cdc-scan.png"
                class="scanner-provision-dialog__barcode"
                alt="DS9308 USB CDC Host programming barcode"
              />
            </div>
            <p class="mb-0">
              Connect the DS9308 to the vehicle's Raspberry Pi using the USB cable, then position the barcode above at the DS9308 until it confirms the scan (unique beep will sound), then attach it to the
              Raspberry Pi in the vehicle's bus app settings.
            </p>
          </v-card-text>
          <v-card-actions class="layout justify-end">
            <v-btn color="primary" flat @click="scannerProvisionDialog = false">Close</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
      <v-layout row wrap>
        <v-flex md6 sm3 pa-2>
          <form @submit.prevent="searchVehicles">
            <v-text-field
                solo
                v-model="searchKeywords"
                append-icon="fal fa-search"
                :rules="[v => !v || v.length >= 2 || 'Min 2 characters']"
                :disabled="apiInProgress"
                type="search"
                placeholder="Search by licence plate, equipment device id/inventory id"
                @click:append="searchVehicles"
            />
          </form>
        </v-flex>
        <v-flex md2 sm3 pa-2>
          <v-select
              clearable
              :items="transportTypes"
              label="Vehicle Type"
              :value="selectedTransportType"
              @change="onFilterByTransportType"
          />
        </v-flex>
        <v-flex md2 sm3 pa-2>
          <v-select
              clearable
              :items="agencies"
              label="Agency"
              :value="selectedAgency"
              @change="onFilterByAgency"
          />
        </v-flex>
        <v-flex md2 sm3 pa-2>
          <v-btn block color="primary" v-if="hasUserType('admin') && !isOnlyInvestor" :disabled="apiInProgress" :to="{ name: 'transportation-accounts-vehicle', params: {vehicleId: 0} }"><v-icon>fal fa-plus</v-icon> &nbsp; Create Vehicle</v-btn>
        </v-flex>
      </v-layout>
      <v-data-table
          :headers="headers"
          :items="vehicles"
          :pagination.sync="pagination"
          :total-items="totalVehicles"
          :loading="apiInProgress"
          :rows-per-page-items="[10, 25, 50]"
          @update:pagination="onPagination"
          class="elevation-1"
          must-sort
      >
        <template v-slot:items="props">
          <tr>
            <td :title="props.item.licensePlateNumber">
              <v-text-field
                  class="text-xs-left text-field--flat pb1"
                  :value="props.item.licensePlateNumber"
                  readonly
                  hide-details
              />
            </td>
            <td class="text-xs-left">{{ props.item.shortcodeId }}</td>
            <td class="text-xs-left" @click.stop>
              <live-passengers-dialog :vehicle="props.item" />
            </td>
            <td class="text-xs-left" :title="props.item.equipmentTitle">
              <v-icon small v-if="props.item.hasEquipment">
                fas fa-tablet-android-alt
              </v-icon>
            </td>
            <!-- <td class="text-xs-left" :title="props.item.currentDriverLabel + ' ' + props.item.currentAssistantLabel">
              <v-text-field
                  class="text-xs-left text-field--flat pb1"
                  :value="props.item.currentDriverLabel"
                  readonly
                  hide-details
              />
              <v-text-field
                  v-show="props.item.currentAssistantLabel"
                  class="text-xs-left text-field--flat"
                  :value="props.item.currentAssistantLabel"
                  readonly
                  hide-details
              />
            </td> -->
            <!-- <td class="text-xs-left">
              {{ props.item.transportType }}
            </td> -->
            <td class="text-xs-left">{{ props.item.agencyId }}</td>
            <td class="text-xs-left">{{ props.item.dateCreatedLabel }}</td>
            <td class="text-xs-left">{{ props.item.dateUpdatedLabel }}</td>
            <td class="justify-center layout px-0">
              <router-link :to="{ name: 'transportation-accounts-vehicle', params: { vehicleId: props.item.id } }">
                <v-btn flat icon title="View vehicle">
                  <v-icon small>
                    fas fa-pencil
                  </v-icon>
                </v-btn>
              </router-link>
              <v-btn flat icon title="Delete vehicle" :disabled="!props.item.isEditable || isOnlyInvestor" :loading="props.item.isDeleteInProgress"  @click.stop="deleteVehicle(props.item)">
                <v-icon small>fas fa-trash-alt</v-icon>
              </v-btn>
            </td>
          </tr>
        </template>
      </v-data-table>
    </div>
    <router-view
      :force-root-view-refresh="forceRootViewRefresh"
      :signed-in-user="signedInUser"
      :selected-account="selectedAccount"
      :set-as-selected-account="setAsSelectedAccount"
      :has-user-type="hasUserType"
      :transport-types="transportTypes"
      :agencies="agencies"
      :has-transportation-role="hasTransportationRole">
    </router-view>
  </div>
</template>

<style>
  .scanner-provision-dialog__barcode-container {
    background: #fff;
    border: 1px solid #ddd;
    margin: 20px auto;
    max-width: 100%;
    padding: 24px;
    text-align: center;
  }

  .scanner-provision-dialog__barcode {
    max-width: 100%;
  }
</style>

<script>
import src from './src'
export default src
</script>
